<template>
	<div>
		<div v-if="loading">
			Loading cart...
		</div>

		<table v-else class="cart-items-table" cellpadding="0" cellspacing="0" border="0">
			<thead v-if="cartItemList.length > 0">
				<tr>
					<th class="cart-item-heading">Item</th>
					<th class="cart-item-generic">Qty</th>
					<th class="cart-item-generic">Total</th>
				</tr>
			</thead>
			<tbody v-if="cartItemList.length > 0">
				<tr v-for="item in cartItemList" :key="item.id">
					<td>
						<div class="cart-item-desc">
							<div class="cart-item-image is-small">
								<a :href="`/product/${item.product.url_slug}`"><img v-if="item.image_url" :src="item.image_url"></a>
							</div>
							
							<div v-if="item.item_type == 'product'" class="cart-item-info">
								<a :href="`/product/${item.product.url_slug}`" class="cart-item-title">{{ item.product.name }}</a>
								
								<div class="item-info-line">
									<strong>Sizing</strong>
									{{ item | displayItemSize('width') }} &times; {{ item | displayItemSize('height') }}
								</div>

								<div class="item-info-line">
									<strong>Room</strong>
									{{ item.room }}
								</div>

								<div class="item-info-line">
									<strong>Control Position</strong>
									{{ item.headrail_controls | capitalize }}
								</div>

								<div class="item-info-line">
									<strong>Mount Position</strong>
									{{ item.mount_pos | capitalize }}
								</div>
								
								<div v-for="opt in item.options" :key="opt.id" class="item-info-line">
									<strong>{{ opt.type_name }}</strong>
									{{ opt.name }}
								</div>

								<template v-if="item.discounts">
									<div v-for="disc in item.discounts" :key="disc.id" class="item-info-line">
										<strong>{{ disc.name }}</strong>
										<template v-if="disc.deduct_type == 'percentage'">{{ disc.deduct_amount }}% off!</template>
										<template v-else-if="disc.deduct_type == 'flat'">{{ disc.deduct_amount | currency }} off!</template>
									</div>
								</template>
							</div>

							<div v-else-if="item.item_type == 'sample'" class="cart-item-info">
								<a :href="`/product/${item.product.url_slug}`" class="cart-item-title">{{ item.product.name }}</a>

								<div class="item-info-line">
									<strong>Sample:</strong>
									{{ item.option.name }}
								</div>
							</div>
						</div>
					</td>
					
					<td class="cart-item-generic">{{ item.qty }}</td>
					<td class="cart-item-generic">{{ item.price | currency }}</td>
				</tr>

				<tr>
					<td></td>
					<td class="cart-total-label is-small">Subtotal:</td>
					<td class="cart-total-amount is-small">{{ summary.subtotal | currency }}</td>
				</tr>

				<template v-if="summary.coupons && summary.coupons.length > 0">
					<tr v-for="coupon in summary.coupons" :key="coupon.id">
						<td colspan="2" class="cart-total-label is-small">
							<ux-btn circle color="text-primary" aria-label="Remove Promo Code" title="Remove PromoCode" @click="removePromoCode(coupon.id)"><i class="fas fa-times"/></ux-btn>
							{{ `${coupon.name} (${coupon.code})` }}:	
						</td>
						<td class="cart-total-amount is-small">{{ coupon.deduct }}</td>
					</tr>
					<tr>
						<td colspan="2" class="cart-total-label is-small">After discount:</td>
						<td class="cart-total-amount is-small">{{ summary.discounted_subtotal | currency }}</td>
					</tr>
				</template>

				<tr v-for="tax in summary.taxes" :key="tax.name">
					<td></td>
					<td class="cart-total-label is-small">{{ tax.name }}:</td>
					<td class="cart-total-amount is-small">{{ tax.amount | currency }}</td>
				</tr>

				<tr>
					<td></td>
					<td class="cart-total-label is-small">Total:</td>
					<td class="cart-total-amount is-small">{{ summary.total | currency }}</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';

import api from 'frontend/api';
import filters from 'common/filters';
import alerts from 'common/alerts';

export default {
	mixins: [filters],

	filters: {
		displayItemSize(item, field)
		{
			return filters.filters.sizeDisplay({ 
				whole: item[`${field}_whole`],
				fractional: item[`${field}_fractional`]
			})
		}
	},

	data()
	{
		return {
			loading: true,
			loadingSummary: true,
		}
	},

	computed: {
		...mapGetters('cart', ['cartItemList']),
		...mapState({
			order: state => state.checkout.order,
			summary: state => state.checkout.summary,
			items: state => state.cart.items,
			totals: state => state.cart.totals,
		})
	},

	watch: {
		'order.paymentMethod': { 
			deep: true,
			handler(newMethod, oldMethod) {
				if (newMethod == null)
					return;

				if (newMethod.country != oldMethod.country || newMethod.region != oldMethod.region)
					this.refreshSummary();
			}
		},

		'order.paymentMethod.billing_addr': {
			deep: true,
			handler(newOrder, oldOrder) {
				if (newOrder == null)
					return;
					
				if (newOrder.country != oldOrder.country || newOrder.region != oldOrder.region)
					this.refreshSummary();
			}
		}
	},

	methods: {
		...mapActions('cart', ['fetchCartItems']),
		...mapActions('checkout', ['fetchCheckoutSummary']),

		async refreshItems()
		{
			this.loading = true;

			try
			{
				await this.fetchCartItems();
			}
			finally
			{
				this.loading = false;
			}
		},

		async refreshSummary()
		{
			this.loadingSummary = true;

			try
			{
				await this.fetchCheckoutSummary();
			}
			finally
			{
				this.loadingSummary = false;
			}
		},

		async removePromoCode(id)
		{
			if (!await alerts.confirm('Are you sure you want to remove this promo code?', 'Remove Promo Code'))
				return;

			this.loadingSummary = true;

			try
			{
				await api.delete(`/checkout/promo_codes/${id}`);
				await this.refreshSummary();
			}
			finally
			{
				this.loadingSummary = false;
			}
		}
	},

	mounted()
	{
		this.refreshItems();
		this.refreshSummary();
	}
}
</script>